import {PERMISSION_TYPE, type PermissionType} from '../../../Permissions';
import * as PERMISSION_GROUPS from '../../admin-tab/permissions-page/PermissionGroups';
import {FEATURE_FLAGS, HIDDEN_FEATURES, MODULE_TYPES, type Seat, SEAT} from '../../../constants';
import {isFeatureHidden} from './CompanySetupUtil';
import {hasFeatureFlag} from './FeatureUtil';
import {hasModule} from './ModuleUtil';
import {type ConnectionEdge} from './type-util/Connection';
import {type PermissionsGroup, type Person, type Profile} from '@/containers/settings/types';

const PERMISSION_CACHE = new Set();
export const PERMISSIONS_SESSION_STORAGE_KEY = 'permissions';

const validatePermission = (permission: PermissionType) => {
	if (!PERMISSION_TYPE[permission]) {
		throw Error(`Unknown permission of type '${permission}'`);
	}
};

const validatePermissionsType = (permissions: PermissionType[]) => {
	if (!Array.isArray(permissions)) {
		throw Error(`Permissions should be an array of Permissions. Permissions was '${permissions}'`);
	}
};

const checkPermission = (permission, permissions) => {
	validatePermission(permission);
	validatePermissionsType(permissions);
	return permissions.some(perm => perm === permission);
};

export const setPermissions = (permissions: PermissionType[]): void => {
	validatePermissionsType(permissions);
	sessionStorage.setItem(PERMISSIONS_SESSION_STORAGE_KEY, JSON.stringify(permissions));
	PERMISSION_CACHE.clear();
	permissions.forEach(permission => PERMISSION_CACHE.add(permission));
};

export const hasPermission = (permission: PermissionType): boolean => {
	validatePermission(permission);

	if (PERMISSION_CACHE.size === 0) {
		const sessionPermissions = sessionStorage.getItem(PERMISSIONS_SESSION_STORAGE_KEY);
		const permissions = sessionPermissions ? JSON.parse(sessionPermissions) : [];
		setPermissions(permissions);
	}

	return PERMISSION_CACHE.has(permission);
};

// Task editing and deletion are the two permissions whose UI gating must stay inert until
// `task_permissions_by_profile` is enabled for the company, because the server-side checks
// (TaskValidationLogic.validateTaskEditPermission / validateTaskDeletePermission) return
// early while the flag is off. Gating the UI on the bare permission disagrees with the
// server on two counts: fields render read-only even though the mutation behind them still
// succeeds, and profiles that carry no permission rows at all — notably the fixed global
// Contributor profile, which is created with none and has no path to be granted any — lose
// every task-editing affordance in accounts that never opted into the feature.
//
// While the flag is off, everyone may edit and delete. Once an account turns it on, the
// per-profile permission decides.
const isTaskPermissionEnforced = (): boolean => hasFeatureFlag(FEATURE_FLAGS.TASK_PERMISSIONS_BY_PROFILE);

export const hasTaskUpdatePermission = (): boolean => !isTaskPermissionEnforced() || hasPermission(PERMISSION_TYPE.TASK_UPDATE);

export const hasTaskDeletePermission = (): boolean => !isTaskPermissionEnforced() || hasPermission(PERMISSION_TYPE.TASK_DELETE);

export const hasSomePermission = (permissions: PermissionType[]): boolean => {
	for (let i = 0; i < permissions.length; i++) {
		if (hasPermission(permissions[i])) {
			return true;
		}
	}
	return false;
};

export const reportPermissions = [
	PERMISSION_TYPE.INSIGHTS_CREATE,
	PERMISSION_TYPE.INSIGHTS_READ,
	PERMISSION_TYPE.INSIGHTS_UPDATE,
	PERMISSION_TYPE.INSIGHTS_DELETE,
];

export const showCost = (): boolean => {
	return hasModule(MODULE_TYPES.FINANCE) && hasPermission(PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION);
};

export const showRevenue = (): boolean => {
	return (
		!isFeatureHidden(HIDDEN_FEATURES.REVENUE) &&
		hasModule(MODULE_TYPES.FINANCE) &&
		hasSomePermission([PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE])
	);
};
export const canApproveAllocation = (): boolean =>
	!hasModule(MODULE_TYPES.PERMISSION_MANAGEMENT) ||
	!hasModule(MODULE_TYPES.ALLOCATION_APPROVE) ||
	hasPermission(PERMISSION_TYPE.ALLOCATION_APPROVE);

// Can be used to check permissions of both persons and profiles
export const personHasPermission = (permission: PermissionType, person: PermissionsGroup): boolean => {
	return checkPermission(permission, person.permissions);
};

export const isClientUser = (): boolean => {
	return hasPermission(PERMISSION_TYPE.IS_CLIENT_USER);
};

export const personIsClientUser = (person: PermissionsGroup): boolean => {
	return personHasPermission(PERMISSION_TYPE.IS_CLIENT_USER, person);
};

export const personIsVirtualUser = (person: PermissionsGroup): boolean => {
	return personHasPermission(PERMISSION_TYPE.IS_VIRTUAL_USER, person);
};

export const personIsCollaboratorUser = (person: {seat: Seat}): boolean => {
	return person.seat === SEAT.COLLABORATOR;
};

export const personHasContributerProfile = (person: Person): boolean => {
	if (!person.profiles?.edges?.length) {
		return false;
	}
	return person.profiles.edges.some(edge => atob(edge.node.id).includes('-4'));
};

export const personIsAdmin = (person: PermissionsGroup): boolean => {
	return personHasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS, person);
};

const sortAlphabetically = (a, b, exceptions = null) => {
	if (exceptions) {
		if (exceptions[a]) return 1;
		else if (exceptions[b]) return -1;
	}
	if (!a) return 1;
	if (!b) return -1;
	return a.localeCompare(b, undefined, {sensitivity: 'base'});
};

const filterBySeat = (profile: Profile, seat?: Seat) => {
	if (!seat) {
		return true;
	}
	return profile.seat === seat;
};

export const getSelectableProfiles = (profiles: ConnectionEdge<Profile>[], seat?: Seat): Profile[] => {
	return (
		profiles
			.map(profile => profile.node)
			.filter(profile => !personIsClientUser(profile) && !profile.disabled)
			.filter(profile => filterBySeat(profile, seat))
			.sort((a, b) => sortAlphabetically(a.name, b.name)) ?? []
	);
};

export const getProfile = (profiles: Profile[], profileId: string): Profile | undefined => {
	return profiles.find(profile => profile.id === profileId);
};

export const getProfileName = (profiles: Profile[], profileId: string): string => {
	const profile = getProfile(profiles, profileId);
	return profile ? profile.name : '';
};

export const hasPermissionGroup = (permissionGroup: PermissionsGroup, permissions: PermissionType[]): boolean => {
	return permissionGroup.permissions.every(permission => permissions.includes(permission));
};

export const getDependingPermissions = (permissionGroup: {dependingPermissionGroups: PermissionsGroup[]}): PermissionType[] => {
	return (permissionGroup.dependingPermissionGroups || []).flatMap(group => group.permissions);
};

export const getDefaultPermissions = (permissionGroup: {dependingPermissionGroups: PermissionsGroup[]}): PermissionType[] => {
	return getDependingPermissions(permissionGroup).concat(
		PERMISSION_GROUPS.PERMISSION_GROUPS()
			.filter(group => group.dependingPermissionGroups?.includes(permissionGroup))
			.flatMap(group => group.permissions)
	);
};

export const dependencyChecked = (permissionGroup: PermissionsGroup, permissions: PermissionType[]): boolean => {
	switch (permissionGroup) {
		case PERMISSION_GROUPS.CREATE_PERSON:
			return false;
		case PERMISSION_GROUPS.ACCESS_ALL_PROJECTS():
			return false;
		case PERMISSION_GROUPS.CREATE_UPDATE_CLIENT:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_PROJECTS, permissions);
		case PERMISSION_GROUPS.SCHEDULING_ACCESS:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_ALLOCATION, permissions);
		case PERMISSION_GROUPS.SEE_MANAGE_INSIGHTS:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_ALLOCATION, permissions);
		case PERMISSION_GROUPS.SEE_MANAGE_INVOICES:
			return false;
		case PERMISSION_GROUPS.SEE_MANAGE_RETAINER_PERIOD:
			return false;
		case PERMISSION_GROUPS.MANAGE_ALLOCATION:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_PROJECTS, permissions);
		case PERMISSION_GROUPS.MANAGE_COMMENTS_ALL:
			return false;
		case PERMISSION_GROUPS.MANAGE_PHASES:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_PROJECTS, permissions);
		case PERMISSION_GROUPS.MANAGE_PROJECTS:
			return (
				hasPermissionGroup(PERMISSION_GROUPS.SEE_MANAGE_RETAINER_PERIOD, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.MANAGE_PROGRAMS(), permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.PROJECT_SCHEDULING_SHARE_CREATE, permissions)
			);
		case PERMISSION_GROUPS.MANAGE_SPRINTS:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_PROJECTS, permissions);
		case PERMISSION_GROUPS.MANAGE_WORKFLOW_COLUMN:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_PROJECTS, permissions);
		case PERMISSION_GROUPS.MANAGE_TIME_OFF:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_TIME_REGISTRATIONS_ALL, permissions);
		case PERMISSION_GROUPS.MANAGE_TIME_REGISTRATIONS_ALL:
			return false;
		case PERMISSION_GROUPS.MANAGE_PROJECT_ASSIGNMENT:
			return hasPermissionGroup(PERMISSION_GROUPS.MANAGE_PROJECTS, permissions);
		case PERMISSION_GROUPS.VIEW_FINANCIAL_INFORMATION:
			return false;
		case PERMISSION_GROUPS.VIEW_FINANCIAL_INFORMATION_REVENUE:
			return (
				hasPermissionGroup(PERMISSION_GROUPS.SEE_MANAGE_RETAINER_PERIOD, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.VIEW_FINANCIAL_INFORMATION, permissions)
			);
		case PERMISSION_GROUPS.VIEW_SISENSE_DASHBOARDS:
			return hasPermissionGroup(PERMISSION_GROUPS.DESIGN_SISENSE_DASHBOARDS, permissions);
		case PERMISSION_GROUPS.READ_ALL_PROJECTS:
			return hasPermissionGroup(PERMISSION_GROUPS.ACCESS_ALL_PROJECTS(), permissions);
		// Ticketing tiers (ACC-495): lock a lower tier while any higher tier that contains it is enabled.
		case PERMISSION_GROUPS.TICKETS_VIEW_GROUP:
			return (
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_CREATE_GROUP, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_ASSIGN_GROUP, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_MANAGE_GROUP, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_ADMIN_GROUP, permissions)
			);
		case PERMISSION_GROUPS.TICKETS_CREATE_GROUP:
			return (
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_ASSIGN_GROUP, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_MANAGE_GROUP, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_ADMIN_GROUP, permissions)
			);
		case PERMISSION_GROUPS.TICKETS_ASSIGN_GROUP:
			return (
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_MANAGE_GROUP, permissions) ||
				hasPermissionGroup(PERMISSION_GROUPS.TICKETS_ADMIN_GROUP, permissions)
			);
		case PERMISSION_GROUPS.TICKETS_MANAGE_GROUP:
			return hasPermissionGroup(PERMISSION_GROUPS.TICKETS_ADMIN_GROUP, permissions);
		default:
			return false;
	}
};
